import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Users, Activity, BarChart3, Package } from "lucide-react";
import { Card } from "@/components/common";

const tones = {
  blue: { chipBg: "bg-brinsa-azul/10", icon: "text-brinsa-azul" },
  green: { chipBg: "bg-brinsa-verde/10", icon: "text-brinsa-verde" },
  yellow: { chipBg: "bg-brinsa-amarillo/10", icon: "text-brinsa-amarillo" },
  purple: { chipBg: "bg-brinsa-morado/10", icon: "text-brinsa-morado" },
} as const;

export interface QuickAction {
  title: string;
  description: string;
  icon: React.ElementType;
  href: string;
  color: keyof typeof tones;
}

interface Props {
  actions?: QuickAction[];
  title?: string;
}

const defaultActions: QuickAction[] = [
  {
    title: "Gestionar Usuarios",
    description: "Crear, editar y administrar usuarios",
    icon: Users,
    href: "/admin/users",
    color: "blue",
  },
  {
    title: "Crear Notificación",
    description: "Enviar mensajes a usuarios",
    icon: Activity,
    href: "/admin/notifications",
    color: "green",
  },
  {
    title: "Ver Reportes",
    description: "Análisis detallado de la plataforma",
    icon: BarChart3,
    href: "/admin/reports",
    color: "purple",
  },
  {
    title: "Gestionar Contenido",
    description: "Administrar productos y categorías",
    icon: Package,
    href: "/admin/content",
    color: "yellow",
  },
];

const QuickActions: React.FC<Props> = ({ actions = defaultActions, title = "Acciones Rápidas" }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <Card title={title}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {actions.map((action, index) => {
            const tone = tones[action.color] || tones.blue;
            return (
              <Link key={action.title} to={action.href} className="group">
                <motion.div
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                  className="p-4 rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all"
                >
                  <div
                    className={`w-10 h-10 ${tone.chipBg} rounded-xl grid place-items-center mb-3 group-hover:scale-105 transition-transform`}
                  >
                    <action.icon className={`w-5 h-5 ${tone.icon}`} />
                  </div>
                  <h3 className="font-medium text-brinsa-azul mb-1 group-hover:text-brinsa-azul/80 transition-colors">
                    {action.title}
                  </h3>
                  <p className="text-sm text-brinsa-azulOscuro/80">{action.description}</p>
                </motion.div>
              </Link>
            );
          })}
        </div>
      </Card>
    </motion.div>
  );
};

export default QuickActions;
